import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack } from '@mui/material';

import { Text, Title, colors, isTruthy } from 'common';

import { AdditionalRow } from './AdditionalRow';
import { RequiredVariableRow } from './RequiredVariableRow';
import { DYNAMIC_PARAMS_FIELD, type DynamicParam, withRequiredVariableRows } from './utils';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

interface DynamicParamsEditorProps {
  requiredVariables: string[];
}

export function DynamicParamsEditor(props: DynamicParamsEditorProps) {
  const { requiredVariables } = props;
  const { value: rawDynamicParams, setValue } =
    useSelectedNodeField<DynamicParam[]>(DYNAMIC_PARAMS_FIELD);

  const dynamicParams = rawDynamicParams ?? [];
  const requiredKeySet = new Set(requiredVariables);

  const additionalRows = dynamicParams
    .map((param, index) => ({ param, index }))
    .filter(({ param }) => !requiredKeySet.has(param.key));

  let nextAppendIndex = dynamicParams.length;
  const requiredRows = requiredVariables.map((key) => {
    const existingIndex = dynamicParams.findIndex((param) => param.key === key);
    const index = existingIndex >= 0 ? existingIndex : nextAppendIndex++;
    return { key, index };
  });
  const hasUnmaterializedRequired = requiredVariables.some(
    (key) => !dynamicParams.some((param) => param.key === key)
  );

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <Title variant="heading5" component="h3" bold>
          Dynamic parameters
        </Title>
        <Text variant="caption" color={colors.extended.gray['45']}>
          Substituted into form field labels and notification scheme content. Values can reference
          upstream block outputs like {`{{SomeBlock.data.field}}`}.
        </Text>
      </Stack>
      {isTruthy(requiredVariables.length) && (
        <Stack gap={1.5}>
          {requiredRows.map(({ key, index }) => (
            <RequiredVariableRow
              key={key}
              variableKey={key}
              index={index}
              onFocus={() => {
                if (hasUnmaterializedRequired) {
                  setValue(withRequiredVariableRows(dynamicParams, requiredVariables));
                }
              }}
            />
          ))}
        </Stack>
      )}
      <Stack gap={1}>
        <Stack gap={1}>
          {additionalRows.map(({ index }) => (
            <AdditionalRow
              key={index}
              index={index}
              onRemove={() => setValue(dynamicParams.filter((_, rowIndex) => rowIndex !== index))}
            />
          ))}
        </Stack>
        <Box>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setValue([...dynamicParams, { key: '', value: '' }])}
          >
            Add variable
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}
